/**
 * 指挥中心 — 五个原生运维 Tab
 * 所有运维查看与操作均在当前工作台完成，不触发管理页面跳转。
 */
import { authFetch } from './api.js';
import { showToast } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { formatBeijingDisplay } from '../time-beijing.js';
import { renderCommandCenterTasks } from './presentation/command-center-tasks.js';
import { renderCommandCenterMaintenance } from './presentation/command-center-maintenance.js';
import { renderCommandCenterOverview } from './presentation/command-center-overview.js';
import { renderCommandCenterPerformance } from './presentation/command-center-performance.js';
import {
  createCommandCenterDrawerMessage,
  createCommandCenterRecordContent,
  createTaskDiagnosticsContent,
  ensureCommandCenterDrawer,
  showCommandCenterDrawer,
} from './presentation/command-center-drawer.js';
import {
  renderCommandCenterEventsShell,
  renderCommandCenterLogs,
  renderCommandCenterLogsError,
  setCommandCenterLogsLoading,
} from './presentation/command-center-events.js';

let primary = 'overview';
let logTab = 'alerts';
let logMode = 'summary';
let logQuery = '';
let logPage = 1;
let taskFilters = { status: '', search: '' };
let cache = { overview: null, services: null, tasks: null, maintenance: null };
let initialized = false;
let controlsBound = false;

const STATUS_LABELS = {
  ok: '正常', success: '成功', completed: '已完成', queued: '排队中', running: '执行中',
  failed: '失败', error: '异常', degraded: '降级', warning: '警告', warn: '警告',
  pending: '待处理', denied: '已拒绝', allowed: '已允许', timeout: '超时', partial: '部分完成',
  unknown: '未采集', unconfigured: '未配置', observed: '已采集', empty: '暂无数据',
  sent: '已发送', server_error: '服务异常', client_error: '请求异常',
  suppressed: '已抑制重复', disabled: '已停用', cancelled: '已取消', fallback: '降级完成',
  critical: '严重', high: '高', medium: '中', low: '低', info: '提示',
};

const OPERATION_LABELS = {
  agent_react_loop: 'AI 对话 / 主推理',
  agent_self_eval: 'AI 对话 / 回答质检',
  agent_episodic_summary: 'AI 对话 / 会话摘要',
  agent_fast_path: 'AI 对话 / 快速响应',
  agent_fallback: 'AI 对话 / 降级响应',
  agent_image_vision: 'AI 对话 / 图片理解',
  skill_self_extractor: 'Agent / 技能提炼',
  skill_extractor: 'Agent / 技能分析',
  memory_compressor: 'Agent / 上下文压缩',
  llm_unlabeled: '未标记调用',
  sorftime_asin_detail: '商品分析 / ASIN 数据查询',
};

const PHASE_LABELS = {
  scraping: '抓取商品数据', building_keywords: '处理关键词', ready: '准备完成',
  completed: '处理完成', failed: '执行失败', submit: '提交任务', poll: '等待结果',
  download: '下载结果', save: '保存结果', generate: '生成内容', translate: '翻译内容',
};

const ACTION_LABELS = {
  registry_deploy: '发布镜像更新', health_reconcile: '释放僵死任务', runtime_settings_update: '更新运行配置',
  collect_submit: '提交旧选品采集（遗留）', collect_completed: '完成旧选品采集（遗留）', collect_failed: '旧选品采集失败（遗留）',
  collect_cancel_request: '申请取消旧采集（遗留）', collect_cancelled: '旧选品采集已取消（遗留）',
  analyze_product: '分析选品商品',
  auth_login_refresh: '刷新 B 线卖家精灵登录', credentials_update: '更新 B 线卖家精灵凭证', model_sync: '同步旧选品模型（遗留）',
  automation_alert: '选品自动告警', automation_preflight_failed: '自动任务预检失败',
  automation_preflight_passed: '自动任务预检通过',
  selection_pipeline_run: 'A 线蓝海管道执行', selection_pipeline_schedule: 'A 线蓝海调度变更',
  mirror_run_create: 'B 线镜像任务创建', mirror_run_complete: 'B 线镜像任务完成', mirror_run_failed: 'B 线镜像任务失败',
  mirror_matrix_start: 'B 线价格分片矩阵启动', mirror_schedule_update: 'B 线镜像调度变更',
};

const PANEL_META = {
  overview: ['运维总览', '平台健康、近窗指标、依赖异常与待办。'],
  services: ['服务与性能', 'HTTP 性能、外部依赖、业务流水线与运行时状态。'],
  tasks: ['任务与队列', '批量任务、图片队列、僵死任务与链路诊断。'],
  events: ['告警与日志', '告警历史、运行异常、管理审计与调用流水。'],
  maintenance: ['发布与维护', '镜像更新、数据库迁移、任务释放与指标留存。'],
};

function normalizedKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[.:-]+/g, '_');
}

/**
 * 指挥中心可见时间统一为北京时间墙钟（无 ISO T / Z）。
 * format: full → YYYY-MM-DD HH:mm；datetime-sec → 含秒。
 */
function formatCcTime(value, format = 'full') {
  if (value == null || value === '') return '—';
  const formatted = formatBeijingDisplay(value, format);
  if (formatted && formatted !== 'N/A') return formatted;
  // 解析失败时尽量去掉 T/Z，避免原始 ISO 直接上屏
  return String(value)
    .replace('T', ' ')
    .replace(/\.\d{3}Z?$/, '')
    .replace(/Z$/, '')
    .trim() || '—';
}

function statusLabel(value) {
  const raw = String(value || '').trim();
  return STATUS_LABELS[normalizedKey(raw)] || raw || '未知';
}

function statusTagClass(value, failures = 0) {
  const key = normalizedKey(value);
  if (['server_error', 'client_error', 'error', 'failed', 'timeout', 'denied'].includes(key)) return 'bad';
  if (failures > 0 || ['unknown', 'warning', 'warn', 'degraded', 'suppressed', 'disabled', 'cancelled', 'fallback', 'partial'].includes(key)) return 'warn';
  return 'ok';
}

function operationLabel(value) {
  const raw = String(value || '').trim();
  return OPERATION_LABELS[normalizedKey(raw)] || raw || '未标记调用';
}

function phaseLabel(value) {
  const raw = String(value || '').trim();
  return PHASE_LABELS[normalizedKey(raw)] || raw.replace(/_/g, ' ') || '未分配阶段';
}

function actionLabel(value) {
  const raw = String(value || '').trim();
  return ACTION_LABELS[normalizedKey(raw)] || raw.replace(/[._-]+/g, ' ') || '未分类操作';
}

function serviceLabel(value) {
  const raw = String(value || '').trim();
  const key = raw.toLowerCase();
  if (key === 'app') return '应用服务';
  if (key === 'mysql') return '主数据库';
  if (key === 'redis') return '缓存服务';
  if (key === 'sif') return '关键词服务';
  if (key === 'selectdb') return '经营数据库';
  if (key === 'llm') return '文本模型';
  if (key === 'market') return '市场数据';
  if (key === 'linkfox') return '商品套图';
  if (key === 'image') return '图片生成';
  if (key === 'listing') return 'Listing 处理';
  if (key === 'agent') return '智能助手';
  if (key === 'auth') return '登录鉴权';
  if (key === 'system') return '系统服务';
  return raw || '未知服务';
}

function providerLabel(value) {
  const raw = String(value || '').trim();
  const key = raw.toLowerCase();
  if (key.includes('linkfox') || key.includes('ziniao')) return '紫鸟商品套图';
  if (key.includes('selectdb')) return '经营数据查询';
  if (key.includes('sellersprite')) return '卖家精灵数据';
  if (key.includes('minimax')) return '文本生成服务';
  if (key.includes('youdao')) return '有道翻译';
  if (key.includes('sif')) return '关键词服务';
  if (key.includes('gemini')) return 'Gemini 模型';
  return raw || '未知依赖';
}

function unavailableLabel(reason) {
  const key = normalizedKey(reason);
  if (key === 'admin_only') return '当前账号不可查看';
  if (key === 'table_missing') return '数据尚未准备完成';
  if (key === 'not_sampled' || key === 'not_ready') return '暂无采样数据';
  if (key === 'error') return '数据暂时不可用';
  return String(reason || '暂无可用数据');
}

function pct(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return '—';
  return `${(Number(rate) * 100).toFixed(2)}%`;
}
function ms(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Math.round(Number(v))}ms`;
}
function duration(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const value = Number(v);
  if (value >= 60_000) return `${(value / 60_000).toFixed(1)}m`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}s`;
  return `${Math.round(value)}ms`;
}
function rangeMinutes() {
  return Number(document.getElementById('cc-range')?.dataset.value || 60);
}

function renderShellMessage(root, className, text) {
  const message = document.createElement('div');
  message.className = className;
  message.textContent = text;
  root.replaceChildren(message);
}

function syncCommandCenterChrome() {
  const [title, subtitle] = PANEL_META[primary] || PANEL_META.overview;
  const titleEl = document.getElementById('cc-page-title');
  const subtitleEl = document.getElementById('cc-page-subtitle');
  const rangeEl = document.getElementById('cc-range');
  if (titleEl) titleEl.textContent = title;
  if (subtitleEl) subtitleEl.textContent = subtitle;
  if (rangeEl) rangeEl.hidden = !['overview', 'services'].includes(primary);
  document.querySelectorAll('#cc-primary-tabs button').forEach((button) => {
    button.classList.toggle('active', button.dataset.ccPanel === primary);
  });
}

export function initCommandCenter() {
  if (!initialized) {
    initialized = true;
    registerViewLoader('command-center', () => loadShell());
    bindCommandCenterGlobalEvents();
  }

  bindCommandCenterControls();
}

function bindCommandCenterControls() {
  if (controlsBound || !document.getElementById('view-command-center')) return;
  controlsBound = true;

  document.getElementById('cc-refresh-btn')?.addEventListener('click', () => {
    cache = { overview: null, services: null, tasks: null, maintenance: null };
    loadShell(true);
  });
  document.getElementById('cc-range')?.addEventListener('click', (e) => {
    const button = e.target.closest('button[data-cc-range]');
    if (!button) return;
    const range = document.getElementById('cc-range');
    range.dataset.value = button.dataset.ccRange || '60';
    range.querySelectorAll('button').forEach((item) => item.classList.toggle('active', item === button));
    cache.services = null;
    if (primary === 'services' || primary === 'overview') loadShell(true);
  });
  document.getElementById('cc-primary-tabs')?.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-cc-panel]');
    if (!btn) return;
    primary = btn.dataset.ccPanel;
    loadShell();
  });
}

function bindCommandCenterGlobalEvents() {
  document.addEventListener('click', (e) => {
    const panel = e.target.closest('[data-cc-panel-go]');
    if (panel) {
      primary = panel.getAttribute('data-cc-panel-go') || 'overview';
      loadShell();
      return;
    }
    const sub = e.target.closest('[data-cc-logtab]');
    if (sub) {
      logTab = sub.getAttribute('data-cc-logtab');
      logPage = 1;
      renderActive();
      return;
    }
    const mode = e.target.closest('[data-cc-log-mode]');
    if (mode) {
      logMode = mode.getAttribute('data-cc-log-mode') === 'detail' ? 'detail' : 'summary';
      logPage = 1;
      renderActive();
      return;
    }
    const group = e.target.closest('[data-cc-log-group]');
    if (group) {
      logMode = 'detail';
      logQuery = group.getAttribute('data-cc-log-group') || '';
      logPage = 1;
      renderActive();
      return;
    }
    const action = e.target.closest('[data-cc-maint-action]');
    if (action) {
      runMaintenanceAction(action.getAttribute('data-cc-maint-action'), action);
      return;
    }
    const jobDiagnostics = e.target.closest('[data-cc-job-diagnostics]');
    if (jobDiagnostics) {
      openTaskDiagnostics(jobDiagnostics.getAttribute('data-cc-job-diagnostics'));
      return;
    }
    const logAction = e.target.closest('[data-cc-log-action]');
    if (logAction) {
      runLogAction(logAction.getAttribute('data-cc-log-action'), logAction);
      return;
    }
    const taskAction = e.target.closest('[data-cc-task-action]');
    if (taskAction) {
      if (taskAction.getAttribute('data-cc-task-action') === 'reset') {
        taskFilters = { status: '', search: '' };
      } else {
        taskFilters = {
          status: document.getElementById('cc-task-status')?.value || '',
          search: document.getElementById('cc-task-search')?.value?.trim() || '',
        };
      }
      cache.tasks = null;
      loadShell(true);
      return;
    }
    const pageBtn = e.target.closest('[data-cc-page]');
    if (pageBtn) {
      logPage = Number(pageBtn.getAttribute('data-cc-page') || 1);
      loadLogsIntoShell();
      return;
    }
    const open = e.target.closest('[data-cc-open]');
    if (open) {
      openDrawer(JSON.parse(open.getAttribute('data-cc-open') || '{}'));
      return;
    }
    if (e.target.id === 'cc-drawer-close' || e.target.id === 'cc-drawer-backdrop') closeDrawer();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || e.target?.id !== 'cc-task-search') return;
    e.preventDefault();
    taskFilters = {
      status: document.getElementById('cc-task-status')?.value || '',
      search: e.target.value?.trim() || '',
    };
    cache.tasks = null;
    loadShell(true);
  });
}

async function loadShell(force = false) {
  const root = document.getElementById('cc-shell');
  if (!root) return;
  syncCommandCenterChrome();
  ensureDrawer();
  try {
    renderShellMessage(root, 'cc-loading', '加载中…');
    if (primary === 'overview') {
      if (!cache.overview || force) {
        const resp = await authFetch('/api/admin/command-center/dashboard');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        cache.overview = await resp.json();
      }
      if (cache.overview?.roleScope === 'admin' && (!cache.services || force)) {
        try {
          const perfResp = await authFetch(`/api/admin/command-center/performance?sinceMinutes=${rangeMinutes()}`);
          cache.services = perfResp.ok ? await perfResp.json() : null;
        } catch {
          cache.services = null;
        }
      }
      renderOverview(root, cache.overview, cache.services);
    } else if (primary === 'services') {
      if (!cache.services || force) {
        const resp = await authFetch(`/api/admin/command-center/performance?sinceMinutes=${rangeMinutes()}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        cache.services = await resp.json();
      }
      renderPerf(root, cache.services);
    } else if (primary === 'tasks') {
      if (!cache.tasks || force) {
        const params = new URLSearchParams();
        if (taskFilters.status) params.set('status', taskFilters.status);
        if (taskFilters.search) params.set('search', taskFilters.search);
        const resp = await authFetch(`/api/admin/command-center/tasks${params.size ? `?${params}` : ''}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        cache.tasks = await resp.json();
      }
      renderTasks(root, cache.tasks);
    } else if (primary === 'events') {
      renderLogsShell(root);
      await loadLogsIntoShell();
    } else if (primary === 'maintenance') {
      if (!cache.maintenance || force) {
        const resp = await authFetch('/api/admin/command-center/maintenance');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        cache.maintenance = await resp.json();
      }
      renderMaintenance(root, cache.maintenance);
    }
  } catch (err) {
    renderShellMessage(root, 'cc-error', `加载失败：${err.message || '未知错误'}`);
    showToast('指挥中心加载失败', 'error');
  }
}

function renderActive() {
  const root = document.getElementById('cc-shell');
  if (!root) return;
  if (primary === 'events') {
    renderLogsShell(root);
    loadLogsIntoShell();
  } else {
    loadShell();
  }
}

function renderOverview(root, data, performance = null) {
  renderCommandCenterOverview(root, data, performance, {
    labelStatus,
    ms,
    pct,
    providerLabel,
    serviceLabel,
    unavailableLabel,
  });
}

function renderPerf(root, data) {
  renderCommandCenterPerformance(root, data, {
    duration,
    ms,
    pct,
    phaseLabel,
    providerLabel,
    unavailableLabel,
  });
}

function renderTasks(root, data) {
  renderCommandCenterTasks(root, data, taskFilters, {
    statusLabel,
    phaseLabel,
    formatCcTime,
  });
}

function renderMaintenance(root, data) {
  renderCommandCenterMaintenance(root, data, {
    actionLabel,
    formatCcTime,
    statusLabel,
    unavailableLabel,
  });
}

async function runMaintenanceAction(action, button) {
  const config = {
    'registry-check': { url: '/api/admin/registry-update/check', body: {}, success: '镜像检查完成' },
    'registry-deploy': { url: '/api/admin/registry-update/deploy', body: {}, success: '更新任务已提交', confirm: '将进入维护并重启服务，确认现在执行更新？' },
    'reconcile-dry': { url: '/api/admin/system-health/reconcile', body: { dryRun: true }, success: '僵死任务预检完成' },
    'reconcile-run': { url: '/api/admin/system-health/reconcile', body: { dryRun: false }, success: '僵死任务释放完成', confirm: '确认释放已判定为僵死的批量任务？' },
  }[action];
  if (!config || (config.confirm && !window.confirm(config.confirm))) return;
  if (button) button.disabled = true;
  try {
    const resp = await authFetch(config.url, { method: 'POST', body: JSON.stringify(config.body) });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(payload.error || payload.reason || `HTTP ${resp.status}`);
    const result = document.getElementById('cc-maint-result');
    if (result) {
      result.hidden = false;
      result.textContent = JSON.stringify(payload, null, 2);
    }
    cache.tasks = null;
    cache.maintenance = null;
    showToast(config.success, 'success');
  } catch (err) {
    showToast(err.message || '维护操作失败', 'error');
  } finally {
    if (button) button.disabled = false;
  }
}

async function runLogAction(action, button) {
  const config = {
    'clear-old': {
      url: '/api/admin/system-errors/clear?days=30',
      confirm: '确认清理 30 天前的系统错误日志？',
      success: '历史错误日志已清理',
    },
    'clear-all': {
      url: '/api/admin/system-errors/clear?confirm=CLEAR',
      confirm: '确认清空全部系统错误日志？该操作不可恢复。',
      success: '系统错误日志已清空',
    },
  }[action];
  if (!config || !window.confirm(config.confirm)) return;
  if (button) button.disabled = true;
  try {
    const resp = await authFetch(config.url, { method: 'DELETE' });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(payload.error || payload.message || `HTTP ${resp.status}`);
    showToast(`${config.success} · ${payload.deleted ?? 0} 条`, 'success');
    logPage = 1;
    await loadLogsIntoShell();
  } catch (err) {
    showToast(err.message || '错误日志清理失败', 'error');
  } finally {
    if (button) button.disabled = false;
  }
}

async function openTaskDiagnostics(jobId) {
  const id = String(jobId || '').trim();
  if (!id) return;
  const title = `任务诊断 · ${id}`;
  showDrawer(title, createCommandCenterDrawerMessage('cc-loading', '正在加载任务链路…'));
  try {
    const resp = await authFetch(`/api/admin/task-diagnostics?jobId=${encodeURIComponent(id)}`);
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
    showDrawer(title, createTaskDiagnosticsContent(data, {
      formatCcTime,
      phaseLabel,
      providerLabel,
      statusLabel,
    }));
  } catch (err) {
    showDrawer(title, createCommandCenterDrawerMessage('cc-error', `任务诊断加载失败：${err.message || '未知错误'}`));
  }
}

function renderLogsShell(root) {
  renderCommandCenterEventsShell(root, {
    mode: logMode,
    query: logQuery,
    tab: logTab,
  });
}

async function loadLogsIntoShell() {
  const box = document.getElementById('cc-logs-table');
  if (!box) return;
  const q = document.getElementById('cc-logs-q')?.value?.trim() || logQuery;
  const queryButton = document.getElementById('cc-logs-go');
  if (queryButton) queryButton.onclick = () => {
    logQuery = document.getElementById('cc-logs-q')?.value?.trim() || '';
    logPage = 1;
    loadLogsIntoShell();
  };
  document.querySelectorAll('#cc-log-subtabs button').forEach((b) => b.classList.toggle('active', b.dataset.ccLogtab === logTab));
  setCommandCenterLogsLoading(box);
  try {
    const params = new URLSearchParams({ tab: logTab, mode: logMode, page: String(logPage), size: '20', q, days: '7' });
    const resp = await authFetch(`/api/admin/command-center/logs?${params}`);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    renderCommandCenterLogs(box, data, {
      actionLabel,
      formatCcTime,
      logGroupLabel,
      ms,
      operationLabel,
      providerLabel,
      serviceLabel,
      statusLabel,
      statusTagClass,
    });
  } catch (err) {
    renderCommandCenterLogsError(box, err.message || '未知错误');
  }
}

function logGroupLabel(tab, value) {
  if (tab === 'product-research' || tab === 'operations' || tab === 'agent-audit') return actionLabel(value);
  if (tab === 'usage') return providerLabel(value);
  if (tab === 'alerts') {
    const key = normalizedKey(value);
    if (key.includes('login')) return '登录状态告警';
    if (key.includes('dependency')) return '外部依赖告警';
    if (key.includes('batch') || key.includes('job')) return '任务运行告警';
  }
  return serviceLabel(value);
}

function labelStatus(s) {
  if (s === 'ok') return '正常';
  if (s === 'degraded' || s === 'warn') return '降级';
  if (s === 'unconfigured') return '未配置';
  if (s === 'error' || s === 'bad') return '故障';
  return s || '未知';
}

function ensureDrawer() {
  ensureCommandCenterDrawer();
}

function showDrawer(title, content) {
  showCommandCenterDrawer(title, content);
}

function openDrawer(row) {
  showDrawer(row.action || row.source || '事件详情', createCommandCenterRecordContent(row, {
    formatCcTime,
    serviceLabel,
    statusLabel,
  }));
}
function closeDrawer() {
  document.getElementById('cc-drawer')?.classList.remove('open');
  document.getElementById('cc-drawer-backdrop')?.classList.remove('open');
}
